
import { Inject, Injectable } from '@angular/core';
import { DtrumApi } from '@dynatrace/dtrum-api-types';
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';

export enum DTRUM_ACTION_NAME {
    STARTUP_INIT_XERO_OAUTH = "startup - xero oauth initialization",
    STARTUP_READ_XERO_OPENID = "startup - retrieve xero openid data",
    STARTUP_INIT_REGISTRATION = "startup - init registration",
    EMAIL_VERIFICATION_CODE_REQUEST = "email verification - code request",
    EMAIL_VERIFICATION_REJECTED = "email verification - domain rejected",
    EMAIL_VERIFICATION_CODE_SUBMIT = "email verification - submit",
    EMAIL_VERIFICATION_ONETIMECODE_SUBMIT = "email verification - otc submit",
    APPLICATIONS_RESUME = "applications - resume",
    APPLICATIONS_START_PREMADE = "applications - start premade",
    APPLICATIONS_START_NEW = "applications - start new",
    APPLICATIONS_ABANDON = "applications - abandon and restart",
    COMPANY_SWITCH_TO_MANUAL = "company search - switch to manual",
    COMPANY_SWITCH_TO_SEARCH = "company name - switch to search",
    COMPANY_SEARCH = "company search lookup",
    BACK_TO_PREVIOUS = "back to previous",
    SUBMIT_FORM = "submit form",
    COMPLETE_APPLICATION = "complete application",
    IDV_START = "begin idv",
    TRANSLATE_COUNTRIES = "load country name translations",
    ADDRESS_SEARCH_GOOGLE = "search address - google places",
    ADDRESS_LOOKUP_GOOGLE = "search address lookup - google places",
    ADDRESS_LOAD_ITA_PROVINCES = "load italian provinces",
    ADDRESS_LOAD_ITA_TOWNS = "load italian towns",
    SAVE_AND_CONTINUE = "save and continue later",
    BACK_TO_APP_LIST = "back to app list"
};

@Injectable()
export class DynatraceHelperService {
    dtrum: DtrumApi;
    loadEndSent: boolean = false;

    actionsRegister: Map<number, string> = new Map<number, DTRUM_ACTION_NAME>();
    currentAction: number;

    constructor(
        private readonly logger: NGXLogger,
        @Inject(APP_CONFIG) private readonly config: AppConfig) {
        this.dtrum = window.dtrum;
    }

    isTrackingEnabled(): boolean {
        if (this.config.enableDynatraceTracking && !this.dtrum) {
            this.logger.warn("Dtrum API is not available.");
        }
        return this.config.enableDynatraceTracking && Boolean(this.dtrum);
    }

    setPage(path: string): number {
        if (!this.isTrackingEnabled()) return;

        const r = window.dtrum?.setPage({
            group: path,
            name: path
        });

        if (!this.loadEndSent) {
            this.logger.debug("DTRUM - SIGNAL LOAD END");
            window.dtrum?.signalLoadEnd();
            this.loadEndSent = true;
        }

        this.logger.debug("DTRUM - SET PAGE", path);

        return r;
    }

    enterAction(name: string): number {
        if (!this.isTrackingEnabled()) return;

        const actionId = window.dtrum?.enterAction(name);

        this.currentAction = actionId;

        this.actionsRegister.set(actionId, name);
        this.logger.debug("DTRUM - ENTER ACTION", name);

        return actionId;
    }

    setActionName(actionId: number, name: string) {
        if (!this.isTrackingEnabled()) return;

        window.dtrum?.actionName(name, actionId);

        const prevName = this.actionsRegister.get(actionId);
        this.actionsRegister.set(actionId, name);

        this.logger.debug("DTRUM - ACTION RENAMED", prevName, name);
    }

    setCurrentActionName(name: string) {
        if (!this.isTrackingEnabled()) return;

        window.dtrum?.actionName(name);
        const currentActionName = this.actionsRegister.get(this.currentAction);
        if (currentActionName) {
            this.logger.debug("DTRUM - CURRENT ACTION RENAMED", currentActionName, name);
            this.actionsRegister.set(this.currentAction, name);
        } else {
            this.logger.debug("DTRUM - CURRENT ACTION RENAMED", "- unknown -", name);
        }
    }

    leaveAction(actionId: number = null) {
        if (!this.isTrackingEnabled()) return;

        const actionName = this.actionsRegister.get(actionId);
        if (actionName) {
            window.dtrum?.leaveAction(actionId);

            if (this.currentAction === actionId) {
                this.currentAction = null;
            }
            this.actionsRegister.delete(actionId);
            this.logger.debug("DTRUM - LEAVE ACTION", actionId, actionName);
        }
    }

    reportError(e: Error | string, actionId?: number) {
        if (!this.isTrackingEnabled()) return;

        window.dtrum?.reportError(e, actionId);

        const actionName = this.actionsRegister.get(actionId);
        this.logger.debug("DTRUM - REPORTING ERROR", actionId, actionName, e);
    }

}
